import React, { useState, useEffect, useCallback } from 'react';
import { MapPinIcon, HomeIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';
import api from '../utils/api';

/**
 * LocationBanner — shows AT CENTRE (green) or HOME MODE (amber) banner.
 * Persists location in sessionStorage. Exposes onLocationChange(loc) callback.
 */
export default function LocationBanner({ onLocationChange }) {
  const [location, setLocation] = useState(() => sessionStorage.getItem('speakiq_location') || null);
  const [tenantName, setTenantName] = useState('your centre');
  const [showPrompt, setShowPrompt] = useState(false);
  const [checking, setChecking] = useState(false);

  // Fetch tenant settings once
  useEffect(() => {
    api.get('/api/student/location').then(r => {
      setTenantName(r.data.tenant_name || 'your centre');
    }).catch(() => {});
  }, []);

  // If no location yet, show prompt after 1s
  useEffect(() => {
    if (!location) {
      const t = setTimeout(() => setShowPrompt(true), 1000);
      return () => clearTimeout(t);
    }
  }, [location]);

  const checkIn = useCallback(async (loc) => {
    setChecking(true);
    try {
      await api.post('/api/student/checkin', { location: loc, method: 'manual' });
      sessionStorage.setItem('speakiq_location', loc);
      setLocation(loc);
      setShowPrompt(false);
      onLocationChange && onLocationChange(loc);
    } catch (e) {
      // Still set locally even if API fails
      sessionStorage.setItem('speakiq_location', loc);
      setLocation(loc);
      setShowPrompt(false);
      onLocationChange && onLocationChange(loc);
    } finally {
      setChecking(false);
    }
  }, [onLocationChange]);

  const changeLocation = () => {
    sessionStorage.removeItem('speakiq_location');
    setLocation(null);
    setShowPrompt(true);
  };

  const atCentre = location === 'centre';
  const atHome = location === 'home';

  // Prompt overlay
  if (showPrompt && !location) {
    return (
      <div style={{
        background: '#fff', border: '2px solid #E5E7EB', borderRadius: 12, padding: '20px 24px',
        marginBottom: 20, boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <MapPinIcon style={{ width: 20, height: 20, color: '#00B4D8' }} />
          <span style={{ fontWeight: 700, fontSize: 15, color: '#0D1B2A' }}>Where are you studying today?</span>
        </div>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6B7280' }}>
          This helps your tutor track attendance and unlocks the right study mode.
        </p>
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={() => checkIn('centre')}
            disabled={checking}
            style={{
              flex: 1, padding: '12px 16px', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg, #10B981, #059669)', color: '#fff',
              fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center', gap: 8
            }}>
            <BuildingOffice2Icon style={{ width: 18, height: 18 }} />
            I'm at {tenantName}
          </button>
          <button
            onClick={() => checkIn('home')}
            disabled={checking}
            style={{
              flex: 1, padding: '12px 16px', borderRadius: 10, border: '2px solid #F59E0B',
              background: '#FFFBEB', color: '#92400E',
              fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center', gap: 8
            }}>
            <HomeIcon style={{ width: 18, height: 18 }} />
            I'm studying at home
          </button>
        </div>
      </div>
    );
  }

  if (!location) return null;

  return (
    <div style={{
      borderRadius: 10, padding: '10px 16px', marginBottom: 16,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      background: atCentre ? 'linear-gradient(90deg, #D1FAE5, #A7F3D0)' : 'linear-gradient(90deg, #FEF3C7, #FDE68A)',
      border: atCentre ? '1.5px solid #6EE7B7' : '1.5px solid #FCD34D',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {atCentre
          ? <BuildingOffice2Icon style={{ width: 18, height: 18, color: '#059669' }} />
          : <HomeIcon style={{ width: 18, height: 18, color: '#D97706' }} />
        }
        <span style={{ fontWeight: 700, fontSize: 13, color: atCentre ? '#064E3B' : '#78350F' }}>
          {atCentre ? `✅ AT CENTRE — ${tenantName}` : '🏠 HOME MODE — AI Practice enabled'}
        </span>
        {atCentre && (
          <span style={{ fontSize: 12, color: '#065F46', marginLeft: 4 }}>
            · Attendance recorded
          </span>
        )}
      </div>
      <button
        onClick={changeLocation}
        style={{
          background: 'none', border: 'none', fontSize: 12,
          color: atCentre ? '#065F46' : '#78350F', cursor: 'pointer',
          textDecoration: 'underline', padding: 0
        }}>
        Change
      </button>
    </div>
  );
}
